import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
  Input,
  Spinner,
} from 'reactstrap';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CONSULTATIONS_TYPES } from '@/configs';
import { MODALS } from '@/configs/modals';
import { useModals } from '@/hooks/useModals';
import { getPrices, getPricesLoaded } from '@/utils/selectors';
import { getDate, isEmpty } from '@/utils/helpers';
import { fetchNutritionServicesApi, fetchPrices } from '@/store/prices/actions';
import { addConsultationsForUser, addNutritionConsultationsForUser } from '@/store/user/actions';


const GiftConsultationDrawer = () => {
  const modalId = MODALS.giftConsultation;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { modalOpen } = useModals();
  const { params, modalClose } = useModals(modalId);
  const { isOpen, user } = params || {};
  const formatDate = 'YYYY-MM-DD';
  const [expiresAt, setExpiresAt] = useState(getDate(new Date(), formatDate));
  const [loading, setLoading] = useState(false);
  const [nutritionServices, setNutritionServices] = useState([]);
  const [isNutritionServicesLoading, setIsNutritionServicesLoading] = useState(false);
  const [isNutritionServicesLoaded, setIsNutritionServicesLoaded] = useState(false);

  const closeModal = () => {
    setLoading(false);
    modalClose(modalId);
  };

  const prices = useSelector(getPrices);
  const isPricesLoaded = useSelector(getPricesLoaded);
  const pricesSorted = useMemo(() => {
    if (isEmpty(prices)) return []
    return prices?.reduce((a, c) => {
      a[c.type] = c;
      return a
    }, {})
  }, [prices])
  const nutritionServicesSorted = useMemo(() => {
    const services = Array.isArray(nutritionServices) ? nutritionServices : nutritionServices?.results || [];
    return services.reduce((items, service) => {
      items[service.type] = service;
      return items;
    }, {});
  }, [nutritionServices]);

  useEffect(() => {
    isEmpty(prices) && !isPricesLoaded && isOpen && dispatch(fetchPrices());
  }, [dispatch, prices, isOpen, isPricesLoaded]);

  useEffect(() => {
    if (!isOpen || isNutritionServicesLoaded) return;

    setIsNutritionServicesLoading(true);
    fetchNutritionServicesApi()
      .then((services) => setNutritionServices(services || []))
      .catch(() => setNutritionServices([]))
      .finally(() => {
        setIsNutritionServicesLoading(false);
        setIsNutritionServicesLoaded(true);
      });
  }, [isNutritionServicesLoaded, isOpen]);

  const handleClickAddConsultation = (serviceId, serviceType, isNutritionService = false) => {
    const serviceTitle = isNutritionService
      ? t(`nutrition.service_types.${serviceType}`, { defaultValue: serviceType })
      : t(`consultations.types_short.${serviceType}`);
    modalOpen(MODALS.confirm, {
      isOpen: true,
      text: t('giftConsultation.confirm_add_text', {
        type: `${serviceTitle}${
          serviceType === CONSULTATIONS_TYPES.subscription && !!expiresAt
            ? ` ${t('giftConsultation.to_date')} ${getDate(expiresAt, 'DD.MM.YYYY')}`
            : ''
        }`,
        client: user?.fullname,
      }),
      onOk: () => {
        if (!!serviceId && user?.id) {
          setLoading(true);
          const addConsultation = isNutritionService
            ? addNutritionConsultationsForUser
            : addConsultationsForUser;
          addConsultation({
            data: {
              user: user.id,
              service: serviceId,
              expires_at: serviceType === CONSULTATIONS_TYPES.subscription ? expiresAt : undefined,
            },
            dispatch,
            onSuccess: () => {
              toast.success(t('giftConsultation.add_success'));
              closeModal();
            },
            onError: () => {
              setLoading(false);
              toast.error(t('giftConsultation.add_error'));
            }
          })
        }
        modalClose(MODALS.confirm);
      },
    });
  }

  const handleChangeExpiresAt = (e) => {
    setExpiresAt(e?.target?.value);
  }

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={closeModal}
      direction="end"
      className={modalId}
    >
      <OffcanvasHeader toggle={closeModal}>
        {t('giftConsultation.modal_title')}
      </OffcanvasHeader>
      <OffcanvasBody>
        <div className="service-price-items">
          <h5 className="mb-3">{t('giftConsultation.psychologist_services')}</h5>
          {Object.values(pricesSorted)?.map(({ type, id }, _idx) => (
            <div className="service-price-item" key={_idx}>
              <span>
                <b>{t(`consultations.types_short.${type}`)}</b>
              </span>
              <span>
                {type === CONSULTATIONS_TYPES.subscription && (
                  <div className="flex aic">
                    <div className="mr-1">
                      {t('giftConsultation.to_date')}
                    </div>
                    <Input
                      name="expires_at"
                      type="date"
                      value={expiresAt}
                      onChange={handleChangeExpiresAt}
                    />
                  </div>
                )}
              </span>
              <span className="td-actions">
                <Button
                  className="btn-icon btn-icon--xs ml-1"
                  type="button"
                  color="primary"
                  title={t('operations.add')}
                  onClick={() => handleClickAddConsultation(id, type)}
                  disabled={loading || (type === CONSULTATIONS_TYPES.subscription && !expiresAt)}
                >
                  <i className="fa fa-plus-circle" />
                </Button>
              </span>
            </div>
          ))}
          {loading && <Spinner color="primary" type="grow" />}
        </div>
        <div className="service-price-items mt-4">
          <h5 className="mb-3">{t('giftConsultation.nutritionist_services')}</h5>
          {Object.values(nutritionServicesSorted)?.map(({ type, id }, _idx) => (
            <div className="service-price-item" key={_idx}>
              <span>
                <b>{t(`nutrition.service_types.${type}`, { defaultValue: type })}</b>
              </span>
              <span />
              <span className="td-actions">
                <Button
                  className="btn-icon btn-icon--xs ml-1"
                  type="button"
                  color="primary"
                  title={t('operations.add')}
                  onClick={() => handleClickAddConsultation(id, type, true)}
                  disabled={loading || isNutritionServicesLoading}
                >
                  <i className="fa fa-plus-circle" />
                </Button>
              </span>
            </div>
          ))}
          {isNutritionServicesLoading && <Spinner color="primary" type="grow" />}
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default GiftConsultationDrawer;
